import React from 'react';
import { Link } from 'react-router-dom';
import { projects } from '../shared/projects';
import { toProjectSlug } from '../utils/projectSlug';
import {
  getProjectDestination,
  getProjectWorkDate,
  getProjectWorkTitle,
} from '../shared/projectLinks';
import usePageMeta from '../utils/usePageMeta';

const notableWorkSlugs = new Set([
  'stateless-desktops',
  'baselayer',
  'harnessgg',
  'boostos-ai',
]);

// `projects` is maintained newest-first, so filtering in place keeps this list
// in the same order as /work instead of the order the slugs were listed in.
const notableWork = projects
  .filter((project) => notableWorkSlugs.has(toProjectSlug(project)))
  .map((project) => ({
    title: getProjectWorkTitle(project),
    date: getProjectWorkDate(project),
    description: project.shortDescription,
    destination: getProjectDestination(project),
  }));

const isInternal = (destination: string) => destination.startsWith('/');

// Nothing on the home page opens a new tab; it is an index, not a launcher.
const ProjectLink: React.FC<{
  destination: string;
  children: React.ReactNode;
}> = ({ destination, children }) =>
  isInternal(destination) ? (
    <Link to={destination}>{children}</Link>
  ) : (
    <a href={destination}>{children}</a>
  );

const Home: React.FC = () => {
  usePageMeta({
    title: 'Vividh Mahajan',
    description:
      'Vividh Mahajan works on computer use and infrastructure for AI agents. Writing, experiments, and projects.',
    path: '/',
  });

  return (
    <main className="essay-home">
      <div className="essay-home__inner">
        <h1 className="essay-home__name">Vividh Mahajan</h1>

        <div className="essay-home__copy">
          <p>
            Currently interning at{' '}
            <a href="https://irisgo.ai/">IrisGo</a>
            , during my undergrad at{' '}
            <a href="https://uwaterloo.ca/">Waterloo</a>
            . Recently I&rsquo;ve worked with great people and teams at{' '}
            <a href="https://www.manulife.com/">Manulife</a>
            ,{' '}
            <a href="https://goqii.com/">GOQii</a>
            ,{' '}
            <a href="https://theinnovationstory.com/">The Innovation Story</a>
            , and{' '}
            <a href="https://electrongroup.com/">Electron Online</a>
            .
          </p>

          <p>
            I love working on problems in computer use and infrastructure for
            AI agents, like sandboxes.
          </p>

          <p className="essay-home__reach-out">
            Reach out! I always want to meet more people with new ideas and
            perspectives, or who want to play chess or tennis. You can find me
            on:
          </p>
        </div>

        <nav className="essay-home__links" aria-label="Elsewhere">
          <a href="https://x.com/Lasdvv">X</a>
          <a href="https://github.com/Lasdw6">GitHub</a>
          <a href="https://linkedin.com/in/vividhm">LinkedIn</a>
        </nav>

        <section className="essay-home__latest" aria-labelledby="notable-work-heading">
          <h2 id="notable-work-heading">Notable Work</h2>

          <ol className="essay-home__latest-list">
            {notableWork.map((project) => (
              <li key={project.title}>
                <div className="essay-home__work-title">
                  <ProjectLink destination={project.destination}>
                    {project.title}
                  </ProjectLink>{' '}
                  <time>({project.date})</time>
                </div>
                {project.description && (
                  <p className="essay-home__work-description">
                    {project.description}
                  </p>
                )}
              </li>
            ))}
          </ol>

          <Link className="essay-home__more-work" to="/work">
            More work
          </Link>
        </section>
      </div>
    </main>
  );
};

export default Home;
